// 入札の「入札制限」(bid-limits) 実設定値ベースの判定ロジック (= 純関数)。
//
// 設定値は settings「入札制限」画面 (docs/specs/bidding/bid-limits.md) が市場別
// (= スポット / 時間前) に管理し、BFF `BidLimitService.GetBidLimitMatrix` が
// (segment × area × side × kind) のセル行として返す。本モジュールはそのセル群から
// (エリア × 売買) 別の解決済み制限を組み立て、札 1 件の価格 / 数量が範囲内かを判定する。
//
// 判定は segment に依存しない (= どの市場の matrix を読むかは呼び出し元が
// `buildBidLimits` に渡す segment で決める) ため、スポット入札画面 (AC-BDL-008) と
// 時間前入札画面 (AC-BDL-014) が同じ純関数を共有する。元は
// `pages/SpotBidding/models/spotBidLimits.ts` に置いていたが、時間前入札画面が 2 つ目の
// 利用者になった時点で共有 directory へ昇格した (= frontend/apps/ebalance/CLAUDE.md
// 「複数 page で実際に使われてから共有 directory へ昇格する」)。
//
// 配置根拠: 入札制限の「参照・検証」は
// bid-limits.md のスコープ外として各入札画面に委譲されており (spec 明記)、backend が
// 承認申請・提出時に二重検証する前提の UX 事前チェック。よって設定値を BFF から取得して
// frontend の純関数で照合する (= backend に入札画面専用の判定 RPC は無く、matrix 値の
// 取得 RPC のみが存在する)。
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { BidKind } from '@ebalance/proto/bfftypes/v1/bid_kind_pb'
import type { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import type { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type {
  BidLimitCell,
  MarketOrderPolicyEntry,
} from '@ebalance/proto/bffuser/v1/bid_limit_model_pb'

/**
 * 解決済みの (エリア × 売買) 制限。各フィールド undefined = 当該 kind は制限未設定
 * (= セル absent、または value unset の「制限なし」明示) で検証しない。
 *
 * proto BidKind は MIN (下限価格 円/kWh) / MAX (上限価格 円/kWh) / QTY (上限数量(1札)
 * MW) / QTY_TOTAL (上限数量(コマ計) MW) の 4 値。 QTY は手動起票の桁ミス防止ガード
 * (札 1 件単位)、 QTY_TOTAL は起票由来を問わない同一 (エリア×コマ×売買) の総量
 * サーキットブレーカー (bid-limits.md §1-2、 AC-BSP-080)。 数量はいずれも上限のみで
 * 下限は設定に存在しない。
 */
export type AreaSideLimit = {
  /** 下限価格 円/kWh (BidKind.MIN)。 */
  priceMin?: number
  /** 上限価格 円/kWh (BidKind.MAX)。 */
  priceMax?: number
  /** 上限数量(1札) MW (BidKind.QTY)。 */
  qtyMax?: number
  /** 上限数量(コマ計) MW (BidKind.QTY_TOTAL、 AC-BSP-080)。 */
  qtyTotalMax?: number
}

/** (エリア × 売買) → 解決済み制限の lookup。key は `${areaCode}|${side}`。 */
export type BidLimits = ReadonlyMap<string, AreaSideLimit>

// lookup は数値 side (Side: BUY=1 / SELL=2) で key 化する。
const limitKey = (areaCode: number, side: number): string =>
  `${areaCode}|${side}`

/**
 * BFF `GetBidLimitMatrix` のセル群から、対象 segment の (エリア × 売買) 別制限を組む。
 *
 * - `cell.bidSegment !== segment` のセルは無視する (= tab 混在防止)。
 * - `cell.value === undefined` (= 制限なし明示) / 非数値のセルは制限に含めない。
 * - 1 件も残らなかった (エリア × 売買) は map に entry を持たない (= 制限未設定)。
 */
export function buildBidLimits(
  cells: readonly BidLimitCell[],
  segment: BidSegment,
): BidLimits {
  const map = new Map<string, AreaSideLimit>()
  for (const cell of cells) {
    if (cell.bidSegment !== segment) continue
    if (cell.value === undefined) continue
    const num = parseFloat(cell.value.value)
    if (!Number.isFinite(num)) continue
    const key = limitKey(cell.areaCode, cell.side)
    const entry = map.get(key) ?? {}
    if (cell.kind === BidKind.MIN) entry.priceMin = num
    else if (cell.kind === BidKind.MAX) entry.priceMax = num
    else if (cell.kind === BidKind.QTY) entry.qtyMax = num
    else if (cell.kind === BidKind.QTY_TOTAL) entry.qtyTotalMax = num
    map.set(key, entry)
  }
  return map
}

/**
 * (エリア × 売買) の制限を引く。undefined = 制限未設定 (= 違反判定しない)。
 */
export function resolveAreaSideLimit(
  limits: BidLimits,
  areaCode: AreaCode,
  side: Side,
): AreaSideLimit | undefined {
  return limits.get(limitKey(areaCode, side))
}

/**
 * (エリア) の成行注文許可可否を引く (AC-BSP-004a、 SoT は `bid-limits.md` §1-1)。
 * `MarketOrderPolicyEntry` は (segment, area) 単位の toggle で買/売共通に適用される
 * (bid-limits domain に side 概念が無い)。 エントリが無いエリアは既定 = 不許可
 * (AC-BDL-001「デフォルト OFF」)。 取得中 / 取得失敗で entries が空の間も同じ既定に
 * 落ちる (= fail-safe。 成行を早まって選ばせない)。
 */
export function isMarketOrderAllowed(
  entries: readonly MarketOrderPolicyEntry[],
  segment: BidSegment,
  areaCode: AreaCode,
): boolean {
  return entries.some(
    (e) => e.bidSegment === segment && e.areaCode === areaCode && e.allowed,
  )
}

/** 単一の入札制限違反の種別。 */
export type BidLimitViolationKind = 'price-min' | 'price-max' | 'volume-max'

/**
 * 札 1 件の価格 / 数量を (エリア × 売買) 制限と照合して違反種別を返す。
 *
 * - `limit === undefined` (= 制限未設定) は常に空配列 (= 違反なし)。
 * - 各 kind が undefined の制限は当該軸を検証しない。
 * - 境界値ちょうど (= priceMin / priceMax / qtyMax と等値) は違反ではない。
 *
 * 数量の単位: 制限 QTY も札の数量も MW (bid-limits.md 「0.1〜9999.9 MW」/ AC-BSP-053)。
 * proto field 名は `volume_mwh` だが格納値はコマ別 MW なので換算は不要 (issue #4285)。
 */
export function checkBidLimit(
  price: number | undefined,
  volumeMw: number | undefined,
  limit: AreaSideLimit | undefined,
): BidLimitViolationKind[] {
  if (limit === undefined) return []
  const out: BidLimitViolationKind[] = []
  if (price !== undefined && Number.isFinite(price)) {
    if (limit.priceMin !== undefined && price < limit.priceMin) {
      out.push('price-min')
    } else if (limit.priceMax !== undefined && price > limit.priceMax) {
      out.push('price-max')
    }
  }
  if (volumeMw !== undefined && Number.isFinite(volumeMw)) {
    if (limit.qtyMax !== undefined && volumeMw > limit.qtyMax) {
      out.push('volume-max')
    }
  }
  return out
}

/**
 * AC-BSP-103 の submit ゲート述語。 札 1 件の数量が (エリア × 売買) の
 * 上限数量(1札) を超えていれば **超過した上限値 (MW)** を、 超えていなければ
 * `undefined` を返す。
 *
 * 通常入札の個別作成 ([[SpotBidCreateModal]]) と編集 ([[IndividualBidModal]]) は
 * 同じ規則で保存をブロックするので、 「制限を引く → 数量軸だけ見る」 の組み立ては
 * ここ 1 箇所に置く (両 modal に逐語コピーすると、 判定規則を変える時に
 * correlated な編集が要る)。 超過時にだけ上限値が返るので、 呼び出し側は
 * 引いた制限を読み直さずにエラー文言の `{max}` を埋められる。
 *
 * 制限未設定 (= 当該 エリア × 売買 に QTY セルが無い) と非数値の数量は
 * `undefined` (= ブロックしない)。 境界値ちょうどは違反ではない
 * ([[checkBidLimit]] と同じ semantics)。
 */
export function perBidVolumeLimitExceeded(
  limits: BidLimits,
  areaCode: AreaCode,
  side: Side,
  volumeMw: number,
): number | undefined {
  const limit = resolveAreaSideLimit(limits, areaCode, side)
  if (!checkBidLimit(undefined, volumeMw, limit).includes('volume-max')) {
    return undefined
  }
  return limit?.qtyMax
}

// ----- 上限数量(コマ計) 超過検出 (スポット AC-BSP-080 / AC-BSP-097、 時間前 AC-BIN-015 ※、 bid-limits.md §1-2) -----
//
// `checkBidLimit` (上限数量(1札)、 札 1 件単位) とは別軸: 同一 (エリア×コマ×売買) の
// 有効札 合計を settings「入札制限」の QTY_TOTAL セル (`AreaSideLimit.qtyTotalMax`) と
// 照合する集計判定。 母集合 (どの札を合算するか) は市場と判定対象ごとに異なる (スポットは
// 約定確定を除き、 時間前は約定済みも数える) ので、 呼び出し元が母集合を組んで渡す。

export type VolumeTotalViolation = {
  areaCode: AreaCode
  side: Side
  timeCode: number
  /** 当該 (エリア×コマ×売買) の合計数量 MW (= 母集合の単純合算)。 */
  actual: number
  /** 当該 (エリア×売買) の上限数量(コマ計) MW (BidKind.QTY_TOTAL)。 */
  limit: number
}

/**
 * コマ計合算に必要な最小の形 (= 市場ごとの行の型 (スポットの入札行・一括作成の生成札・既存札の数量、
 * 時間前の入札行) を同じ関数で合算するために引数を最小化)。 数量は文字列 (proto Decimal
 * string 由来。issue #4285 でコマ別数量は MW 建てに統一済み)。
 */
export type VolumeCountableBid = {
  areaCode: AreaCode
  timeCode: number
  direction: Side
  volumeMwh: string
}

/**
 * 母集合 (`bids`) を (エリア×コマ×売買) でグルーピングし、 合計数量が settings
 * 「入札制限」の 上限数量(コマ計) (QTY_TOTAL) を超えるグループを検出する
 * (スポット AC-BSP-080 / AC-BSP-097、 時間前 AC-BIN-015 ※)。 制限未設定の (エリア×売買) は
 * 判定しない (= 上限数量(1札) と同じ「制限未設定 = 違反なし」規約)。 ちょうど上限は違反では
 * ない。 どのセル (市場) を読むかは呼び出し元が `buildBidLimits` に渡す segment で決まる。
 *
 * 合計は浮動小数の丸め誤差を避けるため 0.1 MW (書込 contract の刻み) へ丸めてから
 * 比較する。 切り捨てでなく round なのは、 合計値そのものを文言に出すので実際より小さく
 * 見せないため。
 */
export const detectVolumeTotalViolations = (
  bids: readonly VolumeCountableBid[],
  limits: BidLimits,
): VolumeTotalViolation[] => {
  const sums = new Map<
    string,
    { areaCode: AreaCode; side: Side; timeCode: number; total: number }
  >()
  for (const b of bids) {
    const key = `${b.timeCode}|${b.areaCode}|${b.direction}`
    const v = Number(b.volumeMwh)
    const entry = sums.get(key)
    if (entry === undefined) {
      sums.set(key, {
        areaCode: b.areaCode,
        side: b.direction,
        timeCode: b.timeCode,
        total: v,
      })
    } else {
      entry.total += v
    }
  }
  const out: VolumeTotalViolation[] = []
  for (const { areaCode, side, timeCode, total } of sums.values()) {
    const limit = resolveAreaSideLimit(limits, areaCode, side)?.qtyTotalMax
    if (limit === undefined) continue
    const rounded = Math.round(total * 10) / 10
    if (rounded > limit) {
      out.push({ areaCode, side, timeCode, actual: rounded, limit })
    }
  }
  return out
}
